import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Button, Checkbox, Cluster, EmptyState, HintMark, Input, Stack, useToast } from "@salesapp/ui";
import { reportSaveFailure, reportSaved } from "../../lib/saveNotice";
import { opsApi, type AppLayout, type FieldDef, type LayoutPage, type LayoutSection } from "../../lib/opsApi";
import { FIELD_HINTS } from "../../help/fieldHints";
import { DesignedRecordFields } from "./DesignedRecordFields";
import { ListWorkspace, RecordWorkspace } from "./RecordWorkspace";
import { optionsCsv } from "./SearchBar";
import {
  DESIGNER_SCREENS,
  FIELD_TYPES,
  SCREEN_PREVIEWS,
  findField,
  normalizeLayoutPages,
  placedFieldKeys,
  withNormalizedLayout,
} from "./recordLayout";
import { useCrmHref } from "../../lib/customerUrl";

function nextSlot(section: LayoutSection, fieldKey: string, colSpan = 1) {
  return {
    field_key: fieldKey,
    canvas_row: section.fields.length + 1,
    col_start: 1,
    col_span: colSpan,
  };
}

function replacePages(layout: AppLayout, pages: LayoutPage[]): AppLayout {
  return { ...layout, layout: { pages: normalizeLayoutPages(pages) } };
}

function RelatedTabPreview({ entity, tabKey }: { entity: string; tabKey: string }) {
  if (entity === "company" && tabKey === "contacts") {
    return (
      <>
        <div className="crm-toolbar">
          <Input className="crm-search" disabled placeholder="Filter contacts on this company…" />
        </div>
        <div className="crm-panel-card">
          <Stack gap={3}>
            <h2 className="crm-section-title">Add contact</h2>
            <p className="crm-muted">This tab lists related people. Place company fields on Details.</p>
          </Stack>
        </div>
        <div className="crm-table-card crm-panel-card">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Work email</th>
                <th>Marketing</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={3} className="crm-muted">No contacts</td>
              </tr>
            </tbody>
          </table>
        </div>
      </>
    );
  }
  if (entity === "company" && tabKey === "activities") {
    return (
      <>
        <div className="crm-panel-card">
          <Stack gap={3}>
            <h2 className="crm-section-title">Log activity</h2>
            <p className="crm-muted">Calls, meetings, and emails for this company belong here.</p>
          </Stack>
        </div>
        <div className="crm-panel-card">
          <EmptyState title="No activities" description="This related list is not a field canvas." />
        </div>
      </>
    );
  }
  if (entity === "company" && tabKey === "leads") {
    return (
      <>
        <div className="crm-toolbar crm-toolbar--split">
          <select className="crm-select" disabled aria-label="Filter leads by status">
            <option>All leads (0)</option>
          </select>
        </div>
        <div className="crm-table-card crm-panel-card">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Source</th>
                <th>Temperature</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={5} className="crm-muted">No leads yet</td>
              </tr>
            </tbody>
          </table>
        </div>
      </>
    );
  }
  if (entity === "lead" && tabKey === "actions") {
    return (
      <div className="crm-panel-card">
        <EmptyState title="Lead actions" description="Convert lives on this tab. Record fields stay on Details." />
      </div>
    );
  }
  return (
    <div className="crm-panel-card">
      <EmptyState title="Related list" description="This tab matches the live record. Place fields on Details." />
    </div>
  );
}

export function AppDesignerPage({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const crmHref = useCrmHref();
  const navigate = useNavigate();
  const [entity, setEntity] = useState("company");
  const [layout, setLayout] = useState<AppLayout | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tabKey, setTabKey] = useState("details");
  const [selectedSectionKey, setSelectedSectionKey] = useState<string | null>(null);
  const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(null);
  const [editingSectionKey, setEditingSectionKey] = useState<string | null>(null);
  const [catalogueDirty, setCatalogueDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = withNormalizedLayout(await opsApi.getLayout(orgId, entity));
      setLayout(next);
      const pages = normalizeLayoutPages(next.layout.pages);
      setTabKey(pages[0]?.key ?? "details");
      setSelectedSectionKey(pages[0]?.sections?.[0]?.key ?? null);
      setSelectedFieldKey(null);
      setEditingSectionKey(null);
      setCatalogueDirty(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Layout could not be loaded.");
    }
  }, [orgId, entity]);

  useEffect(() => {
    void load();
  }, [load]);

  const pages = useMemo(() => (layout ? normalizeLayoutPages(layout.layout.pages) : []), [layout]);
  const activePage = pages.find((page) => page.key === tabKey) ?? pages[0];
  const placed = placedFieldKeys(layout, activePage?.key);
  const preview = SCREEN_PREVIEWS[entity] ?? SCREEN_PREVIEWS.company;
  const selectedField = layout && selectedFieldKey ? findField(layout.fields, selectedFieldKey) : undefined;
  const selectedSlot = activePage?.sections
    ?.flatMap((section) => section.fields.map((slot) => ({ slot, section })))
    .find((item) => item.slot.field_key === selectedFieldKey);
  const editingSection = (activePage?.sections ?? []).find((section) => section.key === editingSectionKey);
  const inspectorOpen = Boolean((selectedField && selectedSlot) || editingSection);

  function updatePages(mutate: (pages: LayoutPage[]) => LayoutPage[]) {
    if (!layout) return;
    setLayout(replacePages(layout, mutate(pages)));
  }

  function targetSectionKey(): string {
    return selectedSectionKey ?? activePage?.sections?.[0]?.key ?? "details";
  }

  function toggle(field: FieldDef) {
    if (!layout || !activePage || activePage.kind === "related") return;
    if (placed.has(field.field_key)) {
      updatePages((current) =>
        current.map((page) =>
          page.key !== activePage.key
            ? page
            : {
                ...page,
                sections: (page.sections ?? []).map((section) => ({
                  ...section,
                  fields: section.fields.filter((item) => item.field_key !== field.field_key),
                })),
              },
        ),
      );
      if (selectedFieldKey === field.field_key) setSelectedFieldKey(null);
      return;
    }
    const sectionKey = targetSectionKey();
    setSelectedSectionKey(sectionKey);
    updatePages((current) =>
      current.map((page) =>
        page.key !== activePage.key
          ? page
          : {
              ...page,
              sections: (page.sections ?? []).map((section) =>
                section.key === sectionKey
                  ? { ...section, fields: [...section.fields, nextSlot(section, field.field_key, field.field_key === "notes" || field.field_type === "json" ? 2 : 1)] }
                  : section,
              ),
            },
      ),
    );
    setSelectedFieldKey(field.field_key);
    setEditingSectionKey(null);
  }

  function patchSelectedSlot(patch: { sectionKey?: string; colSpan?: number }) {
    if (!layout || !activePage || !selectedFieldKey || !selectedSlot) return;
    updatePages((current) =>
      current.map((page) => {
        if (page.key !== activePage.key) return page;
        const without = (page.sections ?? []).map((section) => ({
          ...section,
          fields: section.fields.filter((item) => item.field_key !== selectedFieldKey),
        }));
        const destinationKey = patch.sectionKey ?? selectedSlot.section.key;
        return {
          ...page,
          sections: without.map((section) => {
            if (section.key !== destinationKey) return section;
            return {
              ...section,
              fields: [
                ...section.fields,
                {
                  ...selectedSlot.slot,
                  col_span: patch.colSpan ?? selectedSlot.slot.col_span,
                  canvas_row: section.fields.length + 1,
                },
              ],
            };
          }),
        };
      }),
    );
    if (patch.sectionKey) setSelectedSectionKey(patch.sectionKey);
  }

  function patchCatalogue(nextField: FieldDef) {
    if (!layout) return;
    setLayout({
      ...layout,
      fields: layout.fields.map((item) => (item.field_key === nextField.field_key ? nextField : item)),
    });
    setCatalogueDirty(true);
  }

  function addSection() {
    if (!layout || !activePage || activePage.kind === "related") return;
    const key = `section_${Date.now().toString(36)}`;
    updatePages((current) =>
      current.map((page) =>
        page.key !== activePage.key
          ? page
          : {
              ...page,
              sections: [...(page.sections ?? []), { key, label: "New section", fields: [] }],
            },
      ),
    );
    setSelectedSectionKey(key);
    setEditingSectionKey(key);
    setSelectedFieldKey(null);
  }

  function deleteSection(sectionKey: string) {
    if (!layout || !activePage || activePage.kind === "related") return;
    const sections = activePage.sections ?? [];
    if (sections.length <= 1) return;
    updatePages((current) =>
      current.map((page) =>
        page.key !== activePage.key
          ? page
          : {
              ...page,
              sections: (page.sections ?? []).filter((section) => section.key !== sectionKey),
            },
      ),
    );
    if (selectedSectionKey === sectionKey) {
      const remaining = sections.filter((section) => section.key !== sectionKey);
      setSelectedSectionKey(remaining[0]?.key ?? null);
    }
    if (editingSectionKey === sectionKey) setEditingSectionKey(null);
  }

  function renameSection(sectionKey: string, label: string) {
    updatePages((current) =>
      current.map((page) => ({
        ...page,
        sections: (page.sections ?? []).map((section) => (section.key === sectionKey ? { ...section, label } : section)),
      })),
    );
  }

  async function save() {
    if (!layout) return;
    setError(null);
    try {
      if (catalogueDirty) {
        const result = await opsApi.saveFields(orgId, entity, layout.fields);
        setLayout((current) => (current ? { ...current, fields: result.fields } : current));
        setCatalogueDirty(false);
      }
      setLayout(withNormalizedLayout(await opsApi.saveLayout(orgId, entity, layout.layout)));
      reportSaved(show, "Layout saved.");
    } catch (err) {
      setError(reportSaveFailure(show, err, "Layout could not be saved."));
    }
  }

  async function reset() {
    setError(null);
    try {
      const next = withNormalizedLayout(await opsApi.resetLayout(orgId, entity));
      setLayout(next);
      const nextPages = normalizeLayoutPages(next.layout.pages);
      setTabKey(nextPages[0]?.key ?? "details");
      setSelectedSectionKey(nextPages[0]?.sections?.[0]?.key ?? null);
      setSelectedFieldKey(null);
      setEditingSectionKey(null);
      setCatalogueDirty(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Default layout could not be restored.");
    }
  }

  const available = layout?.fields ?? [];

  return (
    <ListWorkspace
      title="App Designer"
      actions={
        <Cluster gap={2}>
          <Button variant="secondary" onClick={() => navigate(crmHref("/field-options"))}>
            Field options
          </Button>
          <Button variant="secondary" onClick={() => reset()}>Restore default</Button>
          <Button onClick={() => save()}>Save layout</Button>
        </Cluster>
      }
      toolbar={
        <nav className="crm-designer-screens" aria-label="CRM screens">
          {DESIGNER_SCREENS.map((screen) => (
            <button
              key={screen.key}
              type="button"
              className={`crm-designer-screen${entity === screen.key ? " is-active" : ""}`}
              onPointerDown={(event) => {
                if (event.button !== 0) return;
                setEntity(screen.key);
              }}
              onClick={() => setEntity(screen.key)}
            >
              {screen.label}
            </button>
          ))}
        </nav>
      }
    >
      {error && <p className="crm-error">{error}</p>}
      {!layout ? (
        <EmptyState title="Loading layout" description="Field catalogue and canvas load together." />
      ) : (
        <div className={`crm-designer${inspectorOpen ? " has-inspector" : ""}`}>
          <aside className="crm-designer-palette">
            <h2>Available fields</h2>
            <p className="crm-muted">
              {activePage?.kind === "related"
                ? "This tab is a related list on the live record. Switch to Details to place fields."
                : `Tick a field to add it to ${activePage?.sections?.find((section) => section.key === targetSectionKey())?.label ?? "the selected section"}.`}
            </p>
            {available.map((field) => (
              <div
                key={field.field_key}
                className="crm-designer-field"
                onClick={(event) => {
                  if (activePage?.kind === "related") return;
                  if ((event.target as HTMLElement).closest("input")) return;
                  toggle(field);
                }}
              >
                <Checkbox
                  checked={placed.has(field.field_key)}
                  disabled={activePage?.kind === "related"}
                  onChange={() => toggle(field)}
                />
                <span>
                  {field.label}
                  <small>{field.native ? "Native" : "Flex"} · {field.field_type}</small>
                </span>
              </div>
            ))}
          </aside>
          <div className="crm-designer-preview">
            <RecordWorkspace
              preview
              kind={preview.kind}
              objectLabel={preview.objectLabel}
              title={preview.title}
              subtitle={preview.subtitle}
              backTo={crmHref(preview.backTo)}
              backLabel={preview.backLabel}
              highlights={preview.highlights}
              actions={preview.actions.map((action) => (
                <Button key={action.label} variant={action.variant} disabled>
                  {action.label}
                </Button>
              ))}
              tabs={pages.map((page) => ({ key: page.key, label: page.label }))}
              activeTab={activePage?.key ?? tabKey}
              onTabChange={(key) => {
                setTabKey(key);
                const page = pages.find((item) => item.key === key);
                setSelectedSectionKey(page?.sections?.[0]?.key ?? null);
                setSelectedFieldKey(null);
                setEditingSectionKey(null);
              }}
              aside={
                entity === "company" ? (
                  <>
                    <div className="crm-aside-panel">
                      <h2 className="crm-aside-title">Related quick links</h2>
                      <div className="crm-quick-links">
                        <div className="crm-quick-link"><strong>0</strong>Contacts</div>
                        <div className="crm-quick-link"><strong>0</strong>Open leads</div>
                        <div className="crm-quick-link"><strong>0</strong>Activities</div>
                      </div>
                    </div>
                    <div className="crm-aside-panel">
                      <h2 className="crm-aside-title">Pipeline snapshot</h2>
                      <div className="crm-kpi-value">$0</div>
                      <div className="crm-kpi-label">Open pipeline (0)</div>
                    </div>
                    <div className="crm-aside-panel">
                      <h2 className="crm-aside-title">Upcoming & activity</h2>
                      <EmptyState title="No events yet" description="Leads and logged activities appear here." />
                    </div>
                  </>
                ) : entity === "lead" ? (
                  <div className="crm-aside-panel">
                    <h2 className="crm-aside-title">Related</h2>
                    <div className="crm-quick-links">
                      <div className="crm-quick-link"><strong>1</strong>Company</div>
                      <div className="crm-quick-link"><strong>No</strong>Converted</div>
                    </div>
                  </div>
                ) : undefined
              }
            >
              {activePage?.kind === "related" ? (
                <RelatedTabPreview entity={entity} tabKey={activePage.key} />
              ) : (
                <>
                  <DesignedRecordFields
                    layout={{ ...layout, layout: { pages } }}
                    pageKey={activePage?.key}
                    sampleEntity={entity}
                    selectedFieldKey={selectedFieldKey}
                    selectedSectionKey={selectedSectionKey}
                    respectVisibility={false}
                    onSelectField={(fieldKey, sectionKey) => {
                      setSelectedFieldKey(fieldKey);
                      setSelectedSectionKey(sectionKey);
                      setEditingSectionKey(null);
                    }}
                    onSelectSection={(sectionKey) => {
                      setSelectedSectionKey(sectionKey);
                      setEditingSectionKey(null);
                    }}
                    onEditSection={(sectionKey) => {
                      setEditingSectionKey(sectionKey);
                      setSelectedSectionKey(sectionKey);
                      setSelectedFieldKey(null);
                    }}
                    onDeleteSection={deleteSection}
                  />
                  <div className="crm-designer-canvas-actions">
                    <Button variant="secondary" onClick={addSection}>Add section</Button>
                  </div>
                </>
              )}
            </RecordWorkspace>
          </div>
          {editingSection && (
            <aside className="crm-designer-inspector" aria-label="Section">
              <h2>Section</h2>
              <Stack gap={3}>
                <Input
                  label="Section title"
                  tooltip={FIELD_HINTS.designer_section_title}
                  value={editingSection.label}
                  onChange={(event) => renameSection(editingSection.key, event.target.value)}
                />
                {(activePage?.sections?.length ?? 0) > 1 ? (
                  <Button variant="secondary" onClick={() => deleteSection(editingSection.key)}>
                    Delete section
                  </Button>
                ) : (
                  <p className="crm-muted">A layout needs at least one section.</p>
                )}
              </Stack>
            </aside>
          )}
          {selectedField && selectedSlot && !editingSection && (
            <aside className="crm-designer-inspector">
              <h2>Field</h2>
              <Stack gap={3}>
                <Input
                  label="Label"
                  value={selectedField.label}
                  onChange={(event) => patchCatalogue({ ...selectedField, label: event.target.value })}
                />
                <div className="crm-field">
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)" }}>
                    <label htmlFor="designer-field-data-type">Data type</label>
                    <HintMark label="About Data type" text={FIELD_HINTS.designer_field_type} />
                  </span>
                  <select
                    id="designer-field-data-type"
                    className="crm-select"
                    value={selectedField.field_type}
                    disabled={selectedField.native}
                    onChange={(event) => patchCatalogue({ ...selectedField, field_type: event.target.value })}
                  >
                    {FIELD_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                {selectedField.native && <p className="crm-muted">Native fields keep the stored type.</p>}
                {selectedField.field_type === "select" && (
                  <>
                    <Input
                      label="Options (comma-separated)"
                      tooltip={FIELD_HINTS.designer_field_options}
                      value={optionsCsv(selectedField.options)}
                      onChange={(event) =>
                        patchCatalogue({
                          ...selectedField,
                          options: event.target.value.split(",").map((item) => item.trim()).filter(Boolean),
                        })
                      }
                    />
                    <p className="crm-muted">
                      These values appear in live dropdowns. For Lead status they are the board columns and the card status menu.
                    </p>
                  </>
                )}
                <Checkbox
                  checked={selectedField.required}
                  onChange={(event) => patchCatalogue({ ...selectedField, required: event.target.checked })}
                  label="Required"
                  tooltip={FIELD_HINTS.designer_field_required}
                />
                <div className="crm-field">
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)" }}>
                    <label htmlFor="designer-field-section">Section</label>
                    <HintMark label="About Section" text={FIELD_HINTS.designer_field_section} />
                  </span>
                  <select
                    id="designer-field-section"
                    className="crm-select"
                    value={selectedSlot.section.key}
                    onChange={(event) => patchSelectedSlot({ sectionKey: event.target.value })}
                  >
                    {(activePage?.sections ?? []).map((section) => (
                      <option key={section.key} value={section.key}>{section.label}</option>
                    ))}
                  </select>
                </div>
                <Input
                  label="Section title"
                  value={selectedSlot.section.label}
                  readOnly
                  aria-readonly="true"
                  onChange={() => undefined}
                />
                <p className="crm-muted">Click the section title on the canvas to rename it.</p>
                <div className="crm-field">
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)" }}>
                    <label htmlFor="designer-field-width">Width</label>
                    <HintMark label="About Width" text={FIELD_HINTS.designer_field_width} />
                  </span>
                  <select
                    id="designer-field-width"
                    className="crm-select"
                    value={selectedSlot.slot.col_span >= 2 ? "2" : "1"}
                    onChange={(event) => patchSelectedSlot({ colSpan: Number(event.target.value) })}
                  >
                    <option value="1">Half row</option>
                    <option value="2">Full row</option>
                  </select>
                </div>
                <Button variant="secondary" onClick={() => toggle(selectedField)}>Remove from layout</Button>
              </Stack>
            </aside>
          )}
        </div>
      )}
    </ListWorkspace>
  );
}
